import React, { useState, useEffect, useRef } from 'react';
import { TextAnalyzerService, CaseType, TextStats } from '../../lib/services/textAnalyzerService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import './TextAnalyzer.css';

export const TextAnalyzer: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [stats, setStats] = useState<TextStats | null>(null);
  const [operation, setOperation] = useState<'analyze' | 'case' | 'whitespace'>('analyze');
  const [caseType, setCaseType] = useState<CaseType>('upper');
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  const handleProcess = async () => {
    if (!input.trim()) {
      setOutput('');
      setStats(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const processingKey = `${operation}-${caseType}-${input.substring(0, 50)}`;
    processingRef.current = processingKey;

    try {
      if (operation === 'analyze') {
        const result = await TextAnalyzerService.analyze(input);
        if (processingRef.current === processingKey) {
          setStats(result.outputData as TextStats);
          setOutput('');
          setError(null);
        }
      } else if (operation === 'case') {
        const result = await TextAnalyzerService.convertCase(input, caseType);
        if (processingRef.current === processingKey) {
          setOutput(result.outputData as string);
          setStats(null);
          setError(null);
        }
      } else if (operation === 'whitespace') {
        const result = await TextAnalyzerService.normalizeWhitespace(input);
        if (processingRef.current === processingKey) {
          setOutput(result.outputData as string);
          setStats(null);
          setError(null);
        }
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, 'text-analyzer' as any);
        setError(errorMessage);
        setOutput('');
        setStats(null);
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (input) {
      const timer = setTimeout(() => {
        handleProcess();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else {
      setOutput('');
      setStats(null);
      setError(null);
      setIsLoading(false);
    }
  }, [input, operation, caseType]);

  const handleCopy = async () => {
    if (!output) return;

    try {
      const success = await ClipboardUtils.copyText(output);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Result';
    }
  };

  const getButtonClassName = () => {
    const baseClass = 'copy-button';
    if (!output) return `${baseClass} copy-idle`;
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  return (
    <div className="text-analyzer">
      <noscript>
        <div className="fallback-form">
          <h3>JavaScript Required</h3>
          <p>Text analyzer requires JavaScript. Please enable JavaScript to use this tool.</p>
        </div>
      </noscript>

      <div style={{ marginBottom: '2rem' }}>
        <h1>Text Analyzer & Utilities</h1>
        <p>
          Analyze text statistics, convert case formats, and normalize whitespace.
          Supports character/word counting, case conversion, and text cleanup.
        </p>
      </div>

      <div className="encoder-container">
        <div className="form-group">
          <label>
            Operation:
          </label>
          <select
            value={operation}
            onChange={(e) => setOperation(e.target.value as 'analyze' | 'case' | 'whitespace')}
            data-testid="operation-select"
          >
            <option value="analyze">Analyze Text Statistics</option>
            <option value="case">Convert Case</option>
            <option value="whitespace">Normalize Whitespace</option>
          </select>
        </div>

        {operation === 'case' && (
          <div className="form-group">
            <label>
              Case Type:
            </label>
            <select
              value={caseType}
              onChange={(e) => setCaseType(e.target.value as CaseType)}
              data-testid="case-type-select"
            >
              <option value="upper">UPPERCASE</option>
              <option value="lower">lowercase</option>
              <option value="title">Title Case</option>
              <option value="camel">camelCase</option>
              <option value="snake">snake_case</option>
              <option value="kebab">kebab-case</option>
            </select>
          </div>
        )}

        <div className="form-group">
          <label>
            Text Input:
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter text to analyze..."
            data-testid="input-text"
          />
        </div>

        {error && (
          <div className="status-message status-error">
            {error}
          </div>
        )}

        {operation === 'analyze' && stats && (
          <div className="stats-container">
            <h3>Text Statistics</h3>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">Characters:</span>
                <span className="stat-value">{stats.characters}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Characters (no spaces):</span>
                <span className="stat-value">{stats.charactersNoSpaces}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Words:</span>
                <span className="stat-value">{stats.words}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Lines:</span>
                <span className="stat-value">{stats.lines}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Paragraphs:</span>
                <span className="stat-value">{stats.paragraphs}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Sentences:</span>
                <span className="stat-value">{stats.sentences}</span>
              </div>
            </div>
          </div>
        )}

        {(operation === 'case' || operation === 'whitespace') && (
          <div className="form-group">
            <label>
              {operation === 'case' ? 'Converted Text:' : 'Normalized Text:'}
            </label>
            <textarea
              value={output}
              readOnly
              placeholder={isLoading ? 'Processing...' : 'Result will appear here...'}
              className={isLoading ? 'loading' : ''}
              data-testid="output-text"
            />
            {isLoading && (
              <div className="loading-indicator">Processing...</div>
            )}
          </div>
        )}

        {output && (
          <div className="button-container">
            <button
              ref={copyButtonRef}
              onClick={handleCopy}
              disabled={!output}
              className={getButtonClassName()}
              data-testid="copy-button"
            >
              {getButtonText()}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
